import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import {
  getAllReportees,
  createReportee,
  createBulkReportees,
  bulkUploadReportees,
  deleteReportee,
  ReporteeRelationship
} from '@services/Feedback360Service';
import { getAllUsers, User } from '@services/AssessmentService';
import {
  getAllGroups,
  createGroup as createGroupApi,
  deleteGroup as deleteGroupApi,
  assignAssessmentToGroup,
  getGroupAssessments,
  removeAssessmentFromGroup,
  Group,
  GroupAssessment
} from '@services/GroupService';
import { getAllAssessments, Assessment } from '@services/AssessmentEligibilityService';
import { Autocomplete, TextField, Chip, CircularProgress } from '@mui/material';
import './ReporteeManagement.css';
import { Layout } from '@/layout/Layout';
import { useAppTheme } from '@contexts/ThemeContext';
import AddReporteeModal, { ReporteeRelationshipData } from '@components/feedback360/AddReporteeModal';
import BulkUploadRepoteesModal, { BulkReporteeRelationshipData } from '@components/feedback360/BulkUploadRepoteesModal';

const ReporteeManagement: React.FC = () => {
  const { isDarkMode } = useAppTheme();
  const location = useLocation();
  const [preSelectedAssessmentId, setPreSelectedAssessmentId] = useState<number | undefined>(undefined);

  useEffect(() => {
    if (location.state?.assessmentId) {
      setPreSelectedAssessmentId(location.state.assessmentId);
    }
  }, [location.state]);

  const [relationships, setRelationships] = useState<ReporteeRelationship[]>([]);
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const [groups, setGroups] = useState<Group[]>([]);
  const [assessments, setAssessments] = useState<Assessment[]>([]);
  const [expandedGroupId, setExpandedGroupId] = useState<number | null>(null);
  const [groupAssessments, setGroupAssessments] = useState<GroupAssessment[]>([]);
  const [showGroupForm, setShowGroupForm] = useState(false);
  const [newGroupName, setNewGroupName] = useState('');
  const [newGroupDescription, setNewGroupDescription] = useState('');
  const [showAssignAssessmentForm, setShowAssignAssessmentForm] = useState(false);
  const [assignAssessmentId, setAssignAssessmentId] = useState<number | undefined>(undefined);
  const [showDeleteGroupModal, setShowDeleteGroupModal] = useState(false);
  const [deleteGroupTarget, setDeleteGroupTarget] = useState<Group | null>(null);

  const [showAddModal, setShowAddModal] = useState(false);
  const [showBulkUploadModal, setShowBulkUploadModal] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterUser, setFilterUser] = useState<User | null>(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<{
    userId: number;
    reporteeId: number | null;
    reporteeEmail: string;
    userName: string;
    reporteeName: string;
  } | null>(null);

  useEffect(() => {
    loadData();
  }, []);

  useEffect(() => {
    if (successMessage) {
      const timer = setTimeout(() => setSuccessMessage(null), 2000);
      return () => clearTimeout(timer);
    }
  }, [successMessage]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(null), 2000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  const loadData = async () => {
    setLoading(true);
    setError(null);

    try {
      const relationshipsResponse = await getAllReportees();
      if (relationshipsResponse.success && relationshipsResponse.data) {
        setRelationships(relationshipsResponse.data);
      } else {
        throw new Error(relationshipsResponse.error?.message || 'Failed to load relationships');
      }

      const usersResponse = await getAllUsers(1, 1000);
      if (usersResponse.success && usersResponse.data) {
        setUsers(usersResponse.data.users);
      } else {
        throw new Error(usersResponse.error?.message || 'Failed to load users');
      }

      const groupsResponse = await getAllGroups();
      if (groupsResponse.success && groupsResponse.data) {
        setGroups(groupsResponse.data);
      }

      const assessmentsResponse = await getAllAssessments();
      if (assessmentsResponse.success && assessmentsResponse.data) {
        setAssessments(assessmentsResponse.data);
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCreateGroup = async (name?: string) => {
    const groupName = name || newGroupName.trim();
    if (!groupName) return null;

    try {
      const response = await createGroupApi(groupName, newGroupDescription.trim() || undefined);
      if (response.success && response.data) {
        setSuccessMessage(`Group "${groupName}" created successfully!`);
        setShowGroupForm(false);
        setNewGroupName('');
        setNewGroupDescription('');
        const groupsResponse = await getAllGroups();
        if (groupsResponse.success && groupsResponse.data) {
          setGroups(groupsResponse.data);
        }
        return response.data;
      } else {
        throw new Error(response.error?.message || 'Failed to create group');
      }
    } catch (err: any) {
      setError(err.message);
      return null;
    }
  };

  const handleDeleteGroup = async () => {
    if (!deleteGroupTarget) return;

    try {
      const response = await deleteGroupApi(deleteGroupTarget.id);
      if (response.success) {
        setSuccessMessage(`Group "${deleteGroupTarget.name}" deleted successfully!`);
        setShowDeleteGroupModal(false);
        setDeleteGroupTarget(null);
        if (expandedGroupId === deleteGroupTarget.id) {
          setExpandedGroupId(null);
        }
        const groupsResponse = await getAllGroups();
        if (groupsResponse.success && groupsResponse.data) {
          setGroups(groupsResponse.data);
        }
      } else {
        throw new Error(response.error?.message || 'Failed to delete group');
      }
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleExpandGroup = async (groupId: number) => {
    if (expandedGroupId === groupId) {
      setExpandedGroupId(null);
      setGroupAssessments([]);
      setShowAssignAssessmentForm(false);
      return;
    }

    setExpandedGroupId(groupId);
    setShowAssignAssessmentForm(false);

    try {
      const response = await getGroupAssessments(groupId);
      if (response.success && response.data) {
        setGroupAssessments(response.data);
      }
    } catch (err: any) {
      console.error('Error loading group assessments:', err);
    }
  };

  const handleAssignAssessment = async () => {
    if (!expandedGroupId || !assignAssessmentId) return;

    try {
      const response = await assignAssessmentToGroup(expandedGroupId, assignAssessmentId);
      if (response.success) {
        const data = response.data;
        setSuccessMessage(
          `Assessment assigned! ${data?.newly_assigned || 0} users assigned, ${data?.already_eligible || 0} already eligible.`
        );
        setShowAssignAssessmentForm(false);
        setAssignAssessmentId(undefined);
        const assessmentsResponse = await getGroupAssessments(expandedGroupId);
        if (assessmentsResponse.success && assessmentsResponse.data) {
          setGroupAssessments(assessmentsResponse.data);
        }
      } else {
        throw new Error(response.error?.message || 'Failed to assign assessment');
      }
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleRemoveAssessment = async (assessmentId: number) => {
    if (!expandedGroupId) return;

    try {
      const response = await removeAssessmentFromGroup(expandedGroupId, assessmentId);
      if (response.success) {
        setSuccessMessage('Assessment removed from group');
        const assessmentsResponse = await getGroupAssessments(expandedGroupId);
        if (assessmentsResponse.success && assessmentsResponse.data) {
          setGroupAssessments(assessmentsResponse.data);
        }
      } else {
        throw new Error(response.error?.message || 'Failed to remove assessment');
      }
    } catch (err: any) {
      setError(err.message);
    }
  };

  const handleAddRelationship = async (relationshipData: ReporteeRelationshipData) => {
    setLoading(true);
    setError(null);
    setSuccessMessage(null);
    setShowAddModal(false);

    try {
      const existingReporteeIds = relationshipData.reportees
        .filter(r => r.reportee_id !== undefined)
        .map(r => r.reportee_id!);

      const manualReportees = relationshipData.reportees.filter(
        r => r.reportee_name && r.reportee_email
      );

      let successCount = 0;
      let failedCount = 0;

      if (existingReporteeIds.length > 0) {
        const response = await createBulkReportees({
          user_id: relationshipData.user_id,
          reportee_ids: existingReporteeIds
        });

        if (response.success) {
          successCount += existingReporteeIds.length;
        } else {
          failedCount += existingReporteeIds.length;
          throw new Error(response.error?.message || 'Failed to create relationships with existing reportees');
        }
      }

      for (const manualReportee of manualReportees) {
        try {
          const response = await createReportee({
            user_id: relationshipData.user_id,
            reportee_name: manualReportee.reportee_name,
            reportee_email: manualReportee.reportee_email
          });

          if (response.success) {
            successCount++;
          } else {
            failedCount++;
          }
        } catch (err) {
          failedCount++;
          console.error('Error creating manual reportee relationship:', err);
        }
      }

      if (successCount > 0) {
        setSuccessMessage(`Successfully created ${successCount} reportee relationship${successCount > 1 ? 's' : ''}!${failedCount > 0 ? ` (${failedCount} failed)` : ''}`);
        await loadData();
      } else {
        throw new Error('Failed to create any relationships');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to create relationships');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const handleBulkUpload = async (bulkData: BulkReporteeRelationshipData[], assessmentId?: number) => {
    setLoading(true);
    setError(null);
    setSuccessMessage(null);
    setShowBulkUploadModal(false);

    try {
      const response = await bulkUploadReportees({
        relationships: bulkData,
        assessment_id: assessmentId
      });

      if (response.success) {
        const data = response.data?.data || response.data;
        const parts = [];
        if (data?.new_relationships) parts.push(`${data.new_relationships} relationships created`);
        if (data?.created_users) parts.push(`${data.created_users} new users onboarded`);
        if (data?.assessment_assigned) parts.push(`${data.assessment_assigned} assigned to assessment`);

        setSuccessMessage(
          parts.length > 0
            ? `Upload successful! ${parts.join(', ')}.`
            : `Successfully uploaded ${bulkData.length} relationship${bulkData.length > 1 ? 's' : ''}!`
        );
        await loadData();
      } else {
        throw new Error(response.error?.message || 'Failed to upload relationships');
      }
    } catch (err: any) {
      setError(err.message || 'Failed to upload relationships');
      throw err;
    } finally {
      setLoading(false);
    }
  };

  const openDeleteModal = (
    userId: number,
    reporteeId: number | null,
    reporteeEmail: string,
    userName: string,
    reporteeName: string
  ) => {
    setDeleteTarget({ userId, reporteeId, reporteeEmail, userName, reporteeName });
    setShowDeleteModal(true);
  };

  const closeDeleteModal = () => {
    setShowDeleteModal(false);
    setDeleteTarget(null);
  };

  const handleDeleteRelationship = async () => {
    if (!deleteTarget) return;

    setLoading(true);
    setError(null);
    setSuccessMessage(null);
    setShowDeleteModal(false);

    try {
      const response = await deleteReportee(
        deleteTarget.userId,
        deleteTarget.reporteeId,
        deleteTarget.reporteeEmail
      );

      if (response.success) {
        setSuccessMessage('Relationship deleted successfully!');
        await loadData();
      } else {
        throw new Error(response.error?.message || 'Failed to delete relationship');
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setLoading(false);
      setDeleteTarget(null);
    }
  };

  const filteredRelationships = relationships.filter(rel => {
    const matchesSearch = searchTerm === '' ||
      rel.user_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      rel.user_email.toLowerCase().includes(searchTerm.toLowerCase()) ||
      rel.reportee_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      rel.reportee_email.toLowerCase().includes(searchTerm.toLowerCase());

    const matchesFilter = !filterUser || rel.user_id === filterUser.userId;

    return matchesSearch && matchesFilter;
  });

  const readyAssessments = assessments.filter(a => a.status === 'ready');

  return (
    <Layout>
    <div className="reportee-management" data-theme={isDarkMode ? 'dark' : 'light'}>
      {loading && relationships.length > 0 && (
        <div className="reportee-management-loading-overlay">
          <CircularProgress size={60} thickness={4} sx={{ color: '#667eea' }} />
        </div>
      )}

      <div className="reportee-management-page-header">
        <div className="reportee-management-header-content">
          <h1>Reportee Management</h1>
          <p className="reportee-management-subtitle">Manage reportee relationships for 180° feedback assessments</p>
        </div>
        <div className="reportee-management-header-actions">
          <button
            className="reportee-management-btn-secondary"
            onClick={() => setShowBulkUploadModal(true)}
            disabled={loading}
            title="Upload multiple relationships via CSV"
          >
            Bulk Upload
          </button>
          <button
            className="reportee-management-btn-primary"
            onClick={() => setShowAddModal(true)}
            disabled={loading}
          >
            + Add Relationship
          </button>
        </div>
      </div>

      {error && (
        <div className="reportee-management-alert reportee-management-alert-error">
          <span className="reportee-management-alert-icon">!</span>
          {error}
          <button className="reportee-management-alert-close" onClick={() => setError(null)}>×</button>
        </div>
      )}

      {successMessage && (
        <div className="reportee-management-alert reportee-management-alert-success">
          <span className="reportee-management-alert-icon">✓</span>
          {successMessage}
          <button className="reportee-management-alert-close" onClick={() => setSuccessMessage(null)}>×</button>
        </div>
      )}

      <div className="reportee-management-filters-container">
        <div className="reportee-management-search-box">
          <input
            type="text"
            className="reportee-management-form-control"
            placeholder="Search by name or email..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <div className="reportee-management-filter-box">
          <Autocomplete
            options={users}
            getOptionLabel={(option) => option.name}
            value={filterUser}
            onChange={(event, newValue) => {
              setFilterUser(newValue);
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder="Filter by user..."
                variant="outlined"
              />
            )}
            isOptionEqualToValue={(option, value) => option.userId === value.userId}
            sx={{
              width: '100%',
              '& .MuiOutlinedInput-root': {
                padding: '4px',
              }
            }}
          />
        </div>
      </div>

      <div className="reportee-management-relationships-container">
        {loading && relationships.length === 0 ? (
          <div className="reportee-management-loading-state">
            <CircularProgress size={48} thickness={4} sx={{ color: '#667eea', marginBottom: '20px' }} />
            <p>Loading relationships...</p>
          </div>
        ) : filteredRelationships.length === 0 ? (
          <div className="reportee-management-empty-state">
            <div className="reportee-management-empty-icon">📋</div>
            <h3>No Reportee Relationships Found</h3>
            <p>{searchTerm || filterUser ? 'No relationships match your search or filter.' : 'Click "Add Relationship" to create reportee assignments.'}</p>
          </div>
        ) : (
          <div className="reportee-management-table-wrapper">
            <table className="reportee-management-relationships-table">
              <thead>
                <tr>
                  <th>Employee Name</th>
                  <th>Employee Email</th>
                  <th>Reportee Name</th>
                  <th>Reportee Email</th>
                  <th className="reportee-management-text-center">Created Date</th>
                  <th className="reportee-management-text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filteredRelationships.map((rel) => (
                  <tr key={rel.id}>
                    <td>
                      <div className="reportee-management-user-cell">
                        <span className="reportee-management-user-name">{rel.user_name}</span>
                      </div>
                    </td>
                    <td className="reportee-management-email-cell">{rel.user_email}</td>
                    <td>
                      <div className="reportee-management-reportee-cell">
                        <span className="reportee-management-reportee-name">{rel.reportee_name}</span>
                      </div>
                    </td>
                    <td className="reportee-management-email-cell">{rel.reportee_email}</td>
                    <td className="reportee-management-text-center reportee-management-date-cell">
                      {new Date(rel.created_at).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric'
                      })}
                    </td>
                    <td className="reportee-management-text-center">
                      <button
                        className="reportee-management-btn-delete-table"
                        onClick={() => openDeleteModal(
                          rel.user_id,
                          rel.reportee_id,
                          rel.reportee_email,
                          rel.user_name,
                          rel.reportee_name
                        )}
                        disabled={loading}
                        title="Remove relationship"
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {showDeleteModal && deleteTarget && (
        <div className="reportee-management-modal-overlay" onClick={closeDeleteModal}>
          <div className="reportee-management-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="reportee-management-modal-header">
              <h3>Confirm Deletion</h3>
              <button className="reportee-management-modal-close" onClick={closeDeleteModal}>×</button>
            </div>
            <div className="reportee-management-modal-body">
              <p>Are you sure you want to remove this reportee relationship?</p>
              <div className="reportee-management-delete-details">
                <div className="reportee-management-detail-row">
                  <strong>Employee:</strong> {deleteTarget.userName}
                </div>
                <div className="reportee-management-detail-row">
                  <strong>Reportee:</strong> {deleteTarget.reporteeName}
                </div>
              </div>
              <p className="reportee-management-warning-text">This action cannot be undone.</p>
            </div>
            <div className="reportee-management-modal-footer">
              <button className="reportee-management-btn-secondary" onClick={closeDeleteModal}>
                Cancel
              </button>
              <button className="reportee-management-btn-danger" onClick={handleDeleteRelationship}>
                Delete Relationship
              </button>
            </div>
          </div>
        </div>
      )}

      {showDeleteGroupModal && deleteGroupTarget && (
        <div className="reportee-management-modal-overlay" onClick={() => setShowDeleteGroupModal(false)}>
          <div className="reportee-management-modal-content" onClick={(e) => e.stopPropagation()}>
            <div className="reportee-management-modal-header">
              <h3>Delete Group</h3>
              <button className="reportee-management-modal-close" onClick={() => setShowDeleteGroupModal(false)}>×</button>
            </div>
            <div className="reportee-management-modal-body">
              <p>Are you sure you want to delete the group <strong>"{deleteGroupTarget.name}"</strong>?</p>
              <p>This will remove the group association from {deleteGroupTarget.member_count} member(s). Users will not be deleted.</p>
              <p className="reportee-management-warning-text">This action cannot be undone.</p>
            </div>
            <div className="reportee-management-modal-footer">
              <button className="reportee-management-btn-secondary" onClick={() => setShowDeleteGroupModal(false)}>
                Cancel
              </button>
              <button className="reportee-management-btn-danger" onClick={handleDeleteGroup}>
                Delete Group
              </button>
            </div>
          </div>
        </div>
      )}

      <AddReporteeModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
        onSubmit={handleAddRelationship}
        users={users}
        loading={loading}
      />

      <BulkUploadRepoteesModal
        isOpen={showBulkUploadModal}
        onClose={() => setShowBulkUploadModal(false)}
        onSubmit={handleBulkUpload}
        assessments={assessments}
        defaultAssessmentId={preSelectedAssessmentId}
      />
    </div>
    </Layout>
  );
};

export default ReporteeManagement;
